import { AlignLeft, ListChecks } from "lucide-react";
import type { SectionKey } from "../hooks/use-meeting-detail";
import type { CategoryKey, MeetingSpeaker } from "../types";
import { CATEGORY_KEYS, CATEGORY_LABELS } from "../types";
import { Avatar } from "./avatar";
import { Radar } from "./radar";
import { RoleBadge } from "./role-badge";
import { ScoreBar, scoreToneClass } from "./score-bar";

interface SpeakerEntry {
	speakerId: string;
	name: string;
	airtimePct: number; // 0..1
	overallScore: number; // 0..100
	dimensions: Record<CategoryKey, { score: number }>;
	firefliesAnalytics: MeetingSpeaker["firefliesAnalytics"];
	isOrganizer: boolean;
}

/** Formats the Fireflies analytics fields into a compact one-line strip.
 *  Returns null if `fa` is null or all four fields are null.
 *  Individual null fields are omitted — no orphaned separators.
 *  Zero values render (e.g. "0 fillers" is meaningful).
 */
export function formatMeasurements(fa: MeetingSpeaker["firefliesAnalytics"]): string | null {
	if (fa === null || fa === undefined) return null;
	const segments: string[] = [];
	if (fa.fillerWords !== null && fa.fillerWords !== undefined)
		segments.push(`${fa.fillerWords} fillers`);
	if (fa.longestMonologueSec !== null && fa.longestMonologueSec !== undefined)
		segments.push(`${Math.round(fa.longestMonologueSec)}s longest monologue`);
	if (fa.questionsCount !== null && fa.questionsCount !== undefined)
		segments.push(`${fa.questionsCount} questions`);
	if (fa.durationPct !== null && fa.durationPct !== undefined)
		segments.push(`${Math.round(fa.durationPct * 100)}% airtime`);
	if (segments.length === 0) return null;
	return segments.join(" · ");
}

interface DimensionRailProps {
	speakers: SpeakerEntry[];
	activeSpeakerId: string;
	activeDim: CategoryKey | null;
	view: "analyze" | "transcript";
	onSpeakerChange: (id: string) => void;
	onDimChange: (k: CategoryKey | null) => void;
	activeSection: SectionKey;
	onSectionChange: (s: SectionKey) => void;
	actionItemCount: number;
}

export function DimensionRail({
	speakers,
	activeSpeakerId,
	activeDim,
	view,
	onSpeakerChange,
	onDimChange,
	activeSection,
	onSectionChange,
	actionItemCount,
}: DimensionRailProps) {
	const activeSpeaker = speakers.find((s) => s.speakerId === activeSpeakerId) ?? speakers[0];

	// Radar values from active speaker
	const radarValues = CATEGORY_KEYS.map((k) => ({
		label: CATEGORY_LABELS[k].slice(0, 5),
		score: activeSpeaker?.dimensions[k]?.score ?? 0,
	}));

	return (
		<nav className="flex flex-col overflow-y-auto border-r border-hairline bg-canvas px-3 py-4">
			{/* Speakers */}
			<div className="px-2 pb-2">
				<div className="mb-2 flex items-center justify-between">
					<span className="text-eyebrow uppercase text-ink-tertiary">Speakers</span>
					<span className="text-meta text-ink-subtle">{speakers.length}</span>
				</div>
				<div className="space-y-0.5">
					{speakers.map((sp) => {
						const isActive = sp.speakerId === activeSpeakerId;
						// overallScore is 0..100, display as x.xx but use /10 dimension scale
						const displayScore = sp.overallScore / 10;
						return (
							<button
								key={sp.speakerId}
								type="button"
								onClick={() => onSpeakerChange(sp.speakerId)}
								className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
									isActive ? "bg-surface-2" : "hover:bg-surface-1"
								}`}
							>
								<Avatar
									name={sp.name}
									size="md"
									className={`size-[22px] text-[9px] ${isActive ? "ring-1 ring-primary" : ""}`}
								/>
								<div className="min-w-0 flex-1">
									<div
										className={`flex items-center gap-1.5 truncate text-body-sm ${
											isActive ? "font-medium text-ink" : "text-ink-muted"
										}`}
									>
										<span className="truncate">{sp.name.split(" ")[0]}</span>
										<RoleBadge role={sp.isOrganizer ? "organizer" : null} />
									</div>
									<div className="text-meta text-ink-subtle tabular-nums">
										{Math.round(sp.airtimePct * 100)}% airtime
									</div>
								</div>
								<span
									className={`font-mono text-body-sm tabular-nums ${scoreToneClass(displayScore)}`}
								>
									{sp.overallScore.toFixed(0)}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			{/* Sections */}
			<div className="px-2 pb-2 pt-3 mt-3 border-t border-hairline">
				<div className="mb-2">
					<span className="text-eyebrow uppercase text-ink-tertiary">Sections</span>
				</div>
				<div className="space-y-0.5">
					{(
						[
							{ key: "summary" as SectionKey, label: "Summary", Icon: AlignLeft },
							{ key: "action-items" as SectionKey, label: "Action items", Icon: ListChecks },
						] as const
					).map(({ key, label, Icon }) => {
						const isActive = activeDim === null && activeSection === key;
						const count = key === "action-items" ? actionItemCount : null;
						return (
							<button
								key={key}
								type="button"
								onClick={() => onSectionChange(key)}
								className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
									isActive ? "bg-surface-2" : "hover:bg-surface-1"
								}`}
							>
								<Icon className="size-3.5 flex-none text-ink-subtle" />
								<span
									className={`flex-1 truncate text-body-sm ${
										isActive ? "font-medium text-ink" : "text-ink-muted"
									}`}
								>
									{label}
								</span>
								{count !== null ? (
									<span className="ml-auto text-meta tabular-nums text-ink-subtle">{count}</span>
								) : null}
							</button>
						);
					})}
				</div>
			</div>

			{/* Dimensions */}
			<div className="px-2 pb-2 pt-3 mt-3 border-t border-hairline">
				<div className="mb-2">
					<span className="text-eyebrow uppercase text-ink-tertiary">
						Dimensions · {activeSpeaker?.name.split(" ")[0] ?? ""}
					</span>
					{view === "transcript" ? (
						<span className="ml-1 text-meta text-ink-subtle">· filter</span>
					) : null}
				</div>
				<div className="space-y-0.5">
					{CATEGORY_KEYS.map((k) => {
						const score = activeSpeaker?.dimensions[k]?.score ?? 0;
						const isActive = k === activeDim;
						return (
							<button
								key={k}
								type="button"
								onClick={() => onDimChange(k === activeDim ? null : k)}
								className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${isActive ? "bg-surface-2" : "hover:bg-surface-1"}`}
							>
								<span
									className={`w-8 font-mono text-body-sm tabular-nums ${scoreToneClass(score)}`}
								>
									{score.toFixed(1)}
								</span>
								<span
									className={`flex-1 truncate text-body-sm ${isActive ? "font-medium text-ink" : "text-ink-muted"}`}
								>
									{CATEGORY_LABELS[k]}
								</span>
								<div className="w-12 flex-none">
									<ScoreBar value={score} height={3} />
								</div>
							</button>
						);
					})}
				</div>
			</div>

			{/* Snapshot radar */}
			<div className="mt-3 border-t border-hairline px-2 pb-2 pt-4">
				<div className="mb-2">
					<span className="text-eyebrow uppercase text-ink-tertiary">Snapshot</span>
					{view === "transcript" ? (
						<span className="ml-1 text-meta text-ink-subtle">· filter</span>
					) : null}
				</div>
				<div className="flex items-center justify-center rounded-md border border-hairline bg-surface-1 p-2">
					<Radar values={radarValues} size={160} />
				</div>
			</div>
		</nav>
	);
}
